import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  computeInvoiceTotals,
  formatDisplayDate,
  formatMoney,
  lineAmount,
} from '../utils/invoiceModelDefaults';
import { INVOICE_THEME } from '../utils/invoiceDesignTokens';
import { LetterPadFrame } from '../../shared/letterPad';

export default function InvoiceTemplatePreview({ model }) {
  const { subtotal, taxAmount, total, gstPercent } = computeInvoiceTotals(model);

  return (
    <Box
      sx={{
        borderRadius: `${INVOICE_THEME.radiusLg}px`,
        overflow: 'hidden',
        boxShadow: INVOICE_THEME.shadow,
        border: `1px solid ${INVOICE_THEME.border}`,
        fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
        color: INVOICE_THEME.ink,
        position: 'relative',
      }}
    >
      <LetterPadFrame sx={{ borderRadius: 0, boxShadow: 'none' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 3,
            mb: 4,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '0.78rem', color: INVOICE_THEME.slate, fontWeight: 600 }}>
              From
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mt: 0.5 }}>
              Spotless AI
            </Typography>
            {model.companyAddress && (
              <Typography sx={{ fontSize: '0.82rem', color: INVOICE_THEME.slate, mt: 0.5 }}>
                {model.companyAddress}
              </Typography>
            )}
          </Box>

          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography
              sx={{
                fontSize: 'clamp(1.4rem, 2.5vw, 1.85rem)',
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: INVOICE_THEME.ink,
                lineHeight: 1.1,
              }}
            >
              Invoice {model.invoiceNumber || '—'}
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                mt: 1,
                px: 1.25,
                py: 0.35,
                borderRadius: '999px',
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: '#7c3aed',
                bgcolor: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.2)',
              }}
            >
              TAX INVOICE
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
            gap: 3,
            mb: 3.5,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: '14px',
              bgcolor: '#f8fafc',
              border: `1px solid ${INVOICE_THEME.border}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#7c3aed',
                mb: 1,
              }}
            >
              Bill To
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              {model.billToName || '—'}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: INVOICE_THEME.slate, mt: 0.75, whiteSpace: 'pre-line' }}>
              {model.billToAddress || '—'}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: '14px',
              border: `1px solid ${INVOICE_THEME.border}`,
            }}
          >
            {[
              ['Issue date', formatDisplayDate(model.issueDate)],
              ['Due date', formatDisplayDate(model.dueDate)],
              ['Reference', model.reference || '—'],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 0.65,
                  borderBottom: `1px solid ${INVOICE_THEME.border}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ fontSize: '0.8rem', color: INVOICE_THEME.slate }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="thead">
              <Box component="tr" sx={{ background: INVOICE_THEME.gradient }}>
                {['Description', 'Qty', 'Unit price', 'Amount'].map((h) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{
                      px: 2,
                      py: 1.25,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: '#fff',
                      textAlign: h === 'Description' ? 'left' : 'right',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {(model.lineItems || []).map((item) => (
                <Box component="tr" key={item.id}>
                  <Box
                    component="td"
                    sx={{
                      px: 2,
                      py: 1.35,
                      fontSize: '0.88rem',
                      borderBottom: `1px solid ${INVOICE_THEME.border}`,
                    }}
                  >
                    {item.description || '—'}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 2,
                      py: 1.35,
                      fontSize: '0.88rem',
                      textAlign: 'right',
                      borderBottom: `1px solid ${INVOICE_THEME.border}`,
                    }}
                  >
                    {item.quantity}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 2,
                      py: 1.35,
                      fontSize: '0.88rem',
                      textAlign: 'right',
                      borderBottom: `1px solid ${INVOICE_THEME.border}`,
                    }}
                  >
                    {formatMoney(item.unitPrice, model.currency)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 2,
                      py: 1.35,
                      fontSize: '0.88rem',
                      fontWeight: 600,
                      textAlign: 'right',
                      borderBottom: `1px solid ${INVOICE_THEME.border}`,
                    }}
                  >
                    {formatMoney(lineAmount(item), model.currency)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Box
            sx={{
              minWidth: 280,
              p: 2,
              borderRadius: '14px',
              bgcolor: '#f8fafc',
              border: `1px solid ${INVOICE_THEME.border}`,
            }}
          >
            {[
              ['Subtotal', formatMoney(subtotal, model.currency)],
              [`GST ${gstPercent}%`, formatMoney(taxAmount, model.currency)],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '0.88rem', color: '#475569' }}
              >
                <span>{label}</span>
                <span>{value}</span>
              </Box>
            ))}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 1.25,
                mt: 1,
                borderTop: `2px solid ${INVOICE_THEME.border}`,
                fontWeight: 800,
                fontSize: '1.05rem',
                color: INVOICE_THEME.ink,
              }}
            >
              <span>Total ({model.currency || 'INR'})</span>
              <span
                style={{
                  background: INVOICE_THEME.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {formatMoney(total, model.currency)}
              </span>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontSize: '0.78rem', color: INVOICE_THEME.slate, fontWeight: 600 }}>
            Issuer&apos;s signature
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontFamily: '"Segoe Script", "Brush Script MT", cursive',
              fontSize: '1.6rem',
              color: '#334155',
              minHeight: 36,
            }}
          >
            {model.issuerSignature || '\u00A0'}
          </Typography>
        </Box>
      </LetterPadFrame>
    </Box>
  );
}
